import {useState} from 'react';

import Modal from '@/components/Modals';

import {DeliveriesList} from './DeliveriesList';
import useIsLgUp from './useIsLgUp';
import useWebhookDeliveries from './useWebhookDeliveries';
import {WebhookDeliveryDetail} from './WebhookDeliveryDetail';

export type WebhookDeliveriesDisplayProps = {
  businessId: string;
};

// Master-detail for a business's webhook deliveries, following the admin's
// UserLogs screen structure (../../../UserLogs/UserLogsDisplay.tsx):
//   Desktop (lg+): split-pane — compact list on the left, detail pane on the
//     right, both visible at once for comparison.
//   Mobile (<lg): the list is full-width; selecting a row opens the detail in
//     a right-side Modal drawer instead of stacking it below the list.
// The detail pane is only ever mounted on the host matching the current
// viewport (`isLgUp ? selectedWebhookId : null`, inverted in the drawer) so a
// selection never triggers two concurrent detail fetches.
export const WebhookDeliveriesDisplay = ({
  businessId,
}: WebhookDeliveriesDisplayProps) => {
  const {deliveries, hasMore, isLoading, error, filters, setFilters, loadMore} =
    useWebhookDeliveries(businessId);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | null>(
    null,
  );
  const isLgUp = useIsLgUp();

  const handleSelect = (webhookId: string, attempt: string) => {
    setSelectedKey(`${webhookId}-${attempt}`);
    setSelectedWebhookId(webhookId);
  };

  const clearSelection = () => {
    setSelectedKey(null);
    setSelectedWebhookId(null);
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:border lg:border-gray-200 dark:lg:border-dark-borderMid lg:rounded-lg lg:overflow-hidden mt-5">
        <div className="w-full lg:w-2/5 lg:border-r lg:border-gray-200 dark:lg:border-dark-borderMid">
          <DeliveriesList
            deliveries={deliveries}
            selectedKey={selectedKey}
            onSelect={handleSelect}
            filters={filters}
            setFilters={setFilters}
            hasMore={hasMore}
            isLoading={isLoading}
            error={error}
            loadMore={loadMore}
          />
        </div>

        {/* Desktop detail pane — hidden on mobile; the mobile drawer below
            fetches instead when a row is selected on a small viewport. */}
        <div className="hidden lg:block flex-1 lg:max-h-[620px] lg:overflow-y-auto p-4">
          <WebhookDeliveryDetail
            businessId={businessId}
            webhookId={isLgUp ? selectedWebhookId : null}
          />
        </div>
      </div>

      <Modal
        isOpen={!isLgUp && Boolean(selectedWebhookId)}
        onClose={clearSelection}
        title="Delivery detail"
        side="right"
        closeOnOutsideClick
      >
        <WebhookDeliveryDetail
          businessId={businessId}
          webhookId={!isLgUp ? selectedWebhookId : null}
        />
      </Modal>
    </>
  );
};
